import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { AUTH_REQUIRED_EVENT, ApiError, fetchAuthStatus, login } from '../api/client';

type State = 'checking' | 'signed-in' | 'signed-out' | 'error';

/**
 * Shows the login screen until the admin session is valid.
 * Any 401 from the API (e.g. an expired session) sends the user back here.
 */
export function AuthGate({ children }: { children: ReactNode }) {
  const [state, setState] = useState<State>('checking');
  const queryClient = useQueryClient();

  const check = () => {
    setState('checking');
    fetchAuthStatus()
      .then((s) => setState(s.authenticated ? 'signed-in' : 'signed-out'))
      .catch(() => setState('error'));
  };

  useEffect(() => {
    check();
    const onAuthRequired = () => setState('signed-out');
    window.addEventListener(AUTH_REQUIRED_EVENT, onAuthRequired);
    return () => window.removeEventListener(AUTH_REQUIRED_EVENT, onAuthRequired);
  }, []);

  if (state === 'checking') return <div className="auth-screen"><p className="loading-state">Loading…</p></div>;
  if (state === 'error') {
    return (
      <div className="auth-screen">
        <div className="auth-card">
          <h1 className="auth-title">Trading Admin</h1>
          <p className="error-text">Could not reach the server.</p>
          <button className="btn btn--primary auth-submit" onClick={check}>Retry</button>
        </div>
      </div>
    );
  }
  if (state === 'signed-out') {
    return (
      <LoginForm
        onSuccess={() => {
          queryClient.clear();
          setState('signed-in');
        }}
      />
    );
  }
  return <>{children}</>;
}

function LoginForm({ onSuccess }: { onSuccess: () => void }) {
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setPending(true);
    setError(null);
    try {
      await login(password);
      onSuccess();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Login failed');
    } finally {
      setPending(false);
    }
  };

  return (
    <div className="auth-screen">
      <form className="auth-card" onSubmit={submit}>
        <h1 className="auth-title">Trading Admin</h1>
        <p className="auth-subtitle">Sign in to continue.</p>
        {error && <p className="error-message" role="alert">{error}</p>}
        <label className="auth-label" htmlFor="admin-password">Password</label>
        <input
          id="admin-password"
          className="input-field auth-input"
          type="password"
          autoComplete="current-password"
          autoFocus
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
        <button className="btn btn--primary auth-submit" type="submit" disabled={pending || !password}>
          {pending ? 'Signing in…' : 'Sign in'}
        </button>
      </form>
    </div>
  );
}
